import { BridgeComponent } from "@hotwired/hotwire-native-bridge"
import { debounce } from "../../application"

export default class extends BridgeComponent {
    static component = "search"
    static targets = ["searchResultsContainer", "searchFrame", "filtersIcon"]
    static values = {
        toggle: Boolean
    }

    connect() {
        super.connect()

        this.debouncedSearch = debounce((query) => this.performSearch(query), 250)
        this.saveScrollPosition = this.saveScrollPosition.bind(this)
        this.handleNativeMessage = this.handleNativeMessage.bind(this)
        this.resendConnectMessage = this.resendConnectMessage.bind(this)
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this)

        // Mirror the toggle value: true when the overlay survived in the DOM
        // (Turbo snapshot restore on back navigation, morphs), false on fresh
        // page loads. Native compares this against its own state and sends a
        // restore message when the page reloaded fresh while search was open.
        this.searchActivated = this.toggleValue

        // Set up scroll position persistence
        if (this.hasSearchResultsContainerTarget) {
            this.searchResultsContainerTarget.addEventListener('scroll', this.saveScrollPosition)
            this.restoreScrollPosition()
        }

        if (this.appVersionLessThanOrEqual({ ios: '1.67' })) {
            document.querySelector('body').style.setProperty('padding-top', '60px', 'important')
        }

        // Bridge replies target the message that registered them, which can
        // belong to a previous page generation after back navigation. Native
        // (iOS 1.74+) dispatches document events instead so they always reach
        // the live page. The reply callback in sendConnectMessage is kept for
        // older app versions and Android.
        document.addEventListener("app-search:message", this.handleNativeMessage)
        document.addEventListener("app-search:reconnect", this.resendConnectMessage)

        // The native bridge silently drops messages that arrive while the
        // destination fragment/controller is inactive — e.g. when the app is
        // backgrounded during a cold-start page load, this connect message is
        // lost and the native search bar never appears (the native component
        // is only instantiated on its first received message, so its own
        // recreation logic can't help). Resend whenever the page becomes
        // visible again; both native SearchComponents treat a repeat connect
        // as a state update, so this is safe to fire more than once.
        document.addEventListener("visibilitychange", this.handleVisibilityChange)

        this.sendConnectMessage()
        this.preloadSearchFrame()
    }

    disconnect() {
        if (this.hasSearchResultsContainerTarget) {
            this.searchResultsContainerTarget.removeEventListener('scroll', this.saveScrollPosition)
        }

        document.removeEventListener("app-search:message", this.handleNativeMessage)
        document.removeEventListener("app-search:reconnect", this.resendConnectMessage)
        document.removeEventListener("visibilitychange", this.handleVisibilityChange)
    }

    handleVisibilityChange() {
        if (document.visibilityState === "visible") {
            this.sendConnectMessage()
        }
    }

    sendConnectMessage() {
        const placeholder = this.element.dataset.placeholder || "Search"
        this.initialQuery = this.element.dataset.initialQuery || ""
        const filterCount = parseInt(this.element.dataset.filterCount || "0", 10)

        const params = { placeholder, filterCount, searchActive: this.searchActivated }
        if (this.initialQuery) {
            params.initialQuery = this.initialQuery
        }

        // TODO(cleanup): This reply callback duplicates the app-search:message
        // document event listener. It exists for app versions that still reply
        // through the bridge: iOS < 1.74 and all Android versions. Once the
        // minimum supported iOS version is >= 1.74 AND Android's
        // SearchComponent dispatches document events instead of bridge
        // replies, drop the callback here (send with no callback) and remove
        // the matching reply fallback in the native SearchComponents.
        this.send("connect", params, message => this.handleMessageData(message.data))
    }

    resendConnectMessage() {
        this.sendConnectMessage()
    }

    handleNativeMessage(event) {
        this.handleMessageData(event.detail || {})
    }

    handleMessageData(data) {
        if (data.focused !== undefined) {
            this.handleFocusChange(data.focused)
        } else if (data.filters !== undefined) {
            this.handleFiltersRequest()
        } else if (data.dismiss !== undefined) {
            this.handleDismiss()
        } else if (data.restore !== undefined) {
            this.handleRestore(data.query)
        } else if (data.submit !== undefined) {
            this.handleSubmit(data.query)
        } else if (data.query !== undefined) {
            this.handleQueryChange(data.query)
        }
    }

    handleRestore(query) {
        // The page reloaded fresh (e.g. a snapshot cache miss on back
        // navigation) while native still has search open. Reopen the overlay
        // with the saved query so the user keeps their results.
        this.searchActivated = true
        this.showResults()

        // Load unless the frame already holds results for this exact query —
        // the idle preload may have populated it with empty-query results,
        // which must not stand in for the saved query's results.
        const restoreSrc = `/search?search[term]=${encodeURIComponent(query || '')}`
        if (this.hasSearchFrameTarget && !this.searchFrameTarget.src?.endsWith(restoreSrc)) {
            // The results render after the frame loads, so the scroll position
            // restored by showResults had nothing to scroll to yet. Re-apply it
            // once the results are in the DOM.
            this.searchFrameTarget.addEventListener("turbo:frame-load", () => this.restoreScrollPosition(), { once: true })
            this.searchFrameTarget.src = restoreSrc
        }
    }

    // Load the search frame while it's still hidden so opening the overlay is
    // a pure class toggle instead of a blank screen waiting on the network.
    // App-only: on web the overlay is never opened, so the request would be
    // wasted on every pageview.
    preloadSearchFrame() {
        if (!document.body.dataset.platform) return
        if (!this.hasSearchFrameTarget || this.searchFrameTarget.src) return

        const idle = window.requestIdleCallback
            ? window.requestIdleCallback.bind(window)
            : (fn) => setTimeout(fn, 500)
        idle(() => {
            if (this.hasSearchFrameTarget && !this.searchFrameTarget.src) {
                const query = this.element.dataset.initialQuery || ''
                this.searchFrameTarget.src = `/search?search[term]=${encodeURIComponent(query)}`
            }
        }, { timeout: 3000 })
    }

    handleFocusChange(focused) {
        if (focused) {
            this.searchActivated = true
            this.showResults()

            // Only load if frame doesn't already have results
            if (this.hasSearchFrameTarget && !this.searchFrameTarget.src) {
                const query = this.initialQuery || ''
                this.searchFrameTarget.src = `/search?search[term]=${encodeURIComponent(query)}`
            }
        }
    }

    handleFiltersRequest() {
        if (this.hasFiltersIconTarget) {
            const childLink = this.filtersIconTarget.querySelector('a')
            if (childLink) {
                childLink.click()
            }
        }
    }

    handleDismiss() {
        this.searchActivated = false
        this.hideResults()
    }

    handleSubmit(searchTerm) {
        const url = new URL('/products', window.location.origin)
        url.searchParams.set('product_filter[search]', searchTerm)
        window.Turbo.visit(url)
    }

    handleQueryChange(query) {
        if (!this.searchActivated) {
            return
        }

        if (this.toggleValue) {
            this.debouncedSearch(query)
        }
    }

    performSearch(query) {
        if (this.hasSearchFrameTarget) {
            // A new query starts a new list, so show it from the top once it
            // renders. Waiting for the load keeps the old results in place
            // while the request is in flight.
            this.searchFrameTarget.addEventListener("turbo:frame-load", () => this.scrollResultsToTop(), { once: true })
            this.searchFrameTarget.src = `/search?search[term]=${encodeURIComponent(query || '')}`
        }
    }

    scrollResultsToTop() {
        if (this.hasSearchResultsContainerTarget) {
            this.searchResultsContainerTarget.scrollTop = 0
            this.saveScrollPosition()
        }
    }

    showResults() {
        window.scrollTo(0, 0)
        this.toggleValue = true
        this.restoreScrollPosition()
    }

    saveScrollPosition() {
        if (this.hasSearchResultsContainerTarget) {
            const scrollTop = this.searchResultsContainerTarget.scrollTop
            sessionStorage.setItem('searchResultsScrollPosition', scrollTop)
        }
    }

    restoreScrollPosition() {
        const savedPosition = parseInt(sessionStorage.getItem('searchResultsScrollPosition')) || 0
        if (this.hasSearchResultsContainerTarget && savedPosition > 0) {
            requestAnimationFrame(() => {
                this.searchResultsContainerTarget.scrollTop = savedPosition
            })
        }
    }

    hideResults() {
        this.toggleValue = false
    }

    toggleValueChanged() {
        if (this.hasSearchResultsContainerTarget) {
            if (this.toggleValue) {
                this.searchResultsContainerTarget.classList.remove("hidden")
            } else {
                this.searchResultsContainerTarget.classList.add("hidden")
            }
        }
    }

    appVersionLessThanOrEqual({ ios = null, android = null } = {}) {
        if (!ios && !android) return false

        const platform = document.body.dataset.platform
        const appVersion = document.body.dataset.appVersion
        if (!appVersion || !platform) return false

        let targetVersion = null
        if (platform === 'ios' && ios) {
            targetVersion = ios
        } else if (platform === 'android' && android) {
            targetVersion = android
        }

        if (!targetVersion) return false

        const current = appVersion.split('.').map(Number)
        const target = targetVersion.split('.').map(Number)

        for (let i = 0; i < Math.max(current.length, target.length); i++) {
            const c = current[i] || 0
            const t = target[i] || 0
            if (c < t) return true
            if (c > t) return false
        }
        return true // equal
    }
}
